import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as me from '../../me'

import { Api } from './api'
import type { CreateEntity, TypeParams } from './types'

@ngCore.Injectable({ providedIn: 'root' })
export class Collection extends ec.Collection<TypeParams> {
  constructor() {
    const meFeature = ngCore.inject(me.Feature)
    super({
      api: ngCore.inject(Api),
      context$: meFeature.contexts.selectors.zoneContext.asObservable,
      identify: active => active.id,
      name: ec.Name.Dynamic.from('Summit.Archive'),
      plugins: {
        currentEntity: {
          onChangeInCurrentList: (entities, currentEntityIdsLookup) => {
            const lookup = { ...currentEntityIdsLookup }
            if (!entities.length) {
              return lookup
            }

            if (
              lookup[ec.models.Target.DEFAULT] &&
              entities.find(e => e.id === lookup[ec.models.Target.DEFAULT])
            ) {
              return lookup
            }

            lookup[ec.models.Target.DEFAULT] = entities[0].id

            return lookup
          },
        },
      },
    })
  }

  /**
   * Handles field changes for the {@link current} entity by automatically
   * entering edit mode if needed and updating the specified field value.
   * Also updates the archive's date to the current timestamp on any change.
   */
  public currentValueChangeHandler<
    TKey extends keyof Omit<CreateEntity, 'date'>,
  >(source: string, key: TKey, value: ValueLookup<TKey>) {
    const id = this.plugins.currentEntityPlugin.feature.selectors
      .getId()
      .asSignal()

    if (!id) {
      return
    }

    const fullSource = `${source}.${key}.Edited`

    if (
      !this.plugins.editingPlugin.feature.selectors
        .hasEditedEntity(id)
        .asSignal()
    ) {
      this.plugins.editingPlugin.feature.actions.edit
        .from(fullSource)
        .dispatch({
          id,
        })
    }

    this.plugins.editingPlugin.feature.actions.edit.from(fullSource).dispatch({
      edit: archive => {
        if (!archive) {
          return
        }
        if (
          key === 'name' ||
          key === 'description' ||
          key === 'instructions' ||
          key === 'employees'
        ) {
          archive[key] = value
        }

        // Always update the date to current time
        archive.date =
          new ents.core.dms.data.expression.instance.definitions.DmsDate()
      },
    })
  }
}

type ValueLookup<TKey extends keyof Omit<CreateEntity, 'date'>> = {
  [K in keyof CreateEntity]: K extends 'name'
    ? string
    : K extends 'description'
      ? string | undefined
      : K extends 'instructions'
        ? string | undefined
        : K extends 'employees'
          ? readonly ents.core.dms.data.Record.Id[]
          : never
}[TKey]
